/**
 * Extracts a SpriteLegendEntry from a MapLibre symbol layer by reading icon-image
 * and icon-rotate.
 *
 * Supported icon-image forms:
 *   - static string          → single icon
 *   - ["literal", "name"]    → single icon
 *   - ["match", ...]         → one row per match arm
 *   - ["step", ...]          → one row per step
 *
 * icon-rotate is resolved to a static fallback value; data-driven rotation expressions
 * (e.g. `["get", "bearing"]`) resolve to undefined, leaving the icon unrotated in the
 * legend — which is the correct neutral representation.
 */

import type { ExpressionSpecification, LayerSpecification } from "maplibre-gl";

import type { SpriteLegendEntry } from "../types/legend-types";

import { evalNumber } from "./evaluate-expression";
import { getLayerLabel, getSourceLayer } from "./layer-utils";

/**
 * Parses a symbol layer into a SpriteLegendEntry, or returns null if the layer
 * has no icon-image or an unsupported expression form.
 */
export function extractSpriteLegendEntry(layer: LayerSpecification): SpriteLegendEntry | null {
  if (layer.type !== "symbol") return null;

  const iconImage = layer.layout?.["icon-image"];
  if (!iconImage) return null;

  const layerId = layer.id;
  const layerLabel = getLayerLabel(layer);
  const sourceLayer = getSourceLayer(layer);
  const icons: SpriteLegendEntry["icons"] = [];

  // Data-driven rotation (e.g. `["get", "bearing"]`) resolves to undefined here,
  // leaving the icon unrotated — the correct neutral state for a legend.
  const rotation = evalNumber(layer.layout?.["icon-rotate"]);

  // Static icon name
  if (typeof iconImage === "string") {
    icons.push({ label: layerLabel, iconName: iconImage, rotation });
    return { kind: "sprite", layerId, layerLabel, sourceLayer, icons };
  }

  if (!Array.isArray(iconImage)) return null;
  const expr = iconImage as ExpressionSpecification;

  // ["literal", "iconName"]
  if (expr[0] === "literal" && typeof expr[1] === "string") {
    icons.push({ label: layerLabel, iconName: expr[1], rotation });
    return { kind: "sprite", layerId, layerLabel, sourceLayer, icons };
  }

  // ["match", input, val1, icon1, val2, icon2, ..., fallback]
  if (expr[0] === "match") {
    for (let i = 2; i < expr.length - 1; i += 2) {
      const rawLabel = expr[i];
      const label = Array.isArray(rawLabel)
        ? (rawLabel as (string | number | boolean)[]).map(String).join(", ")
        : String(rawLabel);
      icons.push({ label, iconName: String(expr[i + 1]), rotation });
    }
    icons.push({ label: "other", iconName: String(expr[expr.length - 1]), rotation });
    return { kind: "sprite", layerId, layerLabel, sourceLayer, icons };
  }

  // ["step", input, default_icon, stop1, icon1, ...]
  if (expr[0] === "step") {
    const firstStop = expr[3];
    icons.push({ label: `< ${firstStop}`, iconName: String(expr[2]), rotation });
    for (let i = 3; i < expr.length - 1; i += 2) {
      icons.push({ label: String(expr[i]), iconName: String(expr[i + 1]), rotation });
    }
    return { kind: "sprite", layerId, layerLabel, sourceLayer, icons };
  }

  console.warn(
    `Unsupported sprite legend expression for icon-image in layer ${layer.id}:`,
    iconImage
  );

  return null;
}
